import * as Sentry from '@sentry/nextjs';
import { useEffect, useMemo, useState } from 'react';
import { OperationResult, UseQueryState } from 'urql';
import { tw } from 'twind';
import { Button, Icon } from '@global-ecom/nitro-uds/elements';

import { AddToCartVariables, useCart } from 'hooks/useCart';
import { useWishlist } from 'hooks/useWishlist';
import { withAuth } from 'ui/components/withAuth';
import { TransType, useTranslate } from 'hooks/useTranslations';
import { Page } from 'ui/components/Page';
import {
  MutationRemoveItemFromWishListArgs,
  MutationUpdateItemInWishListArgs,
  WishList,
  WishListItem,
  useMeQuery,
} from '__generated__/graphql';
import { EditProductModal } from 'ui/components/EditProductModal';
import { Link } from 'ui/elements/Link';
import { MiniCart } from 'ui/components/MiniCart';
import { AnalyticsEvents, event } from 'utils/analytics';
import { useMiniCart } from 'hooks/useMiniCart';
import { Img } from 'ui/components/Img';
import { buildProductUrl } from 'utils/buildProductUrl';
import { AvailabilityBadge } from 'ui/components/AvailabilityBadge';
import { useAuth } from 'hooks/useAuth';
import { Price } from 'ui/components/Price';
import { isNumber } from 'utils/isNumber';
import { PageHead } from 'ui/components/PageHead';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { longDateFormat } from 'utils/dateCalculations';
import { RemoveWishlistItemModal } from 'ui/components/RemoveWishlistItemModal';
import { AddToCartButton } from 'ui/components/AddToCartButton';
import { usePageEventsContext } from 'hooks/usePageEventsContext';

type WishlistItemProps = {
  showImage?: boolean;
  item: WishListItem;
  basketId?: string;
  update?: (
    variables: MutationUpdateItemInWishListArgs
  ) => Promise<OperationResult>;
  remove?: (
    variables: MutationRemoveItemFromWishListArgs
  ) => Promise<OperationResult>;
  add?: (variables: AddToCartVariables) => Promise<OperationResult>;
};

const WishlistItem = ({
  item,
  add,
  update,
  remove,
  showImage = true,
}: WishlistItemProps) => {
  const t = useTranslate();
  const {
    locale,
    staticFeatures: { showHeaderSubHeaderProductDetail },
  } = useSiteConfig();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const { product, quantity, id, creationDate } = item;
  const { openMiniCart } = useMiniCart();
  const outOfStock = (product.inventory || 0) < quantity;
  const isSale =
    isNumber(product.salePrice) && product.salePrice !== product.price;
  const isPresale = !!(
    product.displayOutOfStock?.presale && product.displayOutOfStock?.validTo
  );
  const { pageviewEventHasFired } = usePageEventsContext();
  const productUrl = useMemo(
    () =>
      buildProductUrl(product.masterId, product.name, {
        color: product.colorValue,
        ...(product.size.value && { size: product.size.value }),
      }),
    [product]
  );

  const onAddToCart = async () => {
    try {
      if (!add) throw new Error('No add function found');
      setAdding(true);
      const res = await add({
        productId: product.id,
        quantity: quantity,
        swatch: product.colorValue,
        masterId: product.masterId,
      });
      if (res.error) throw res.error;
      if (res.data) {
        const items = res.data.addItemsToCart.products?.filter(
          p =>
            p.__typename === 'ProductCartItem' && p?.product.id === product.id
        );
        if (pageviewEventHasFired) {
          event(AnalyticsEvents.GA4_CustomEvent, {
            event_name: AnalyticsEvents.WISHLIST_TRANSFER,
            event_params: {
              item_id_ep: product.id,
              item_name_ep: product.name,
            },
          });
        }
        if (items) openMiniCart(items);
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setAdding(false);
    }
  };

  const canAddToCart = Boolean(
    product?.orderable &&
      (!product.displayOutOfStock || isPresale) &&
      !outOfStock &&
      !product.offline
  );

  const isSalePriceElapsed = product?.productPrice?.isSalePriceElapsed;

  return (
    <>
      <div key={product.id} className="flex items-center flex-grow sm:flex-col">
        <div className="sm:flex font-display w-full relative">
          <div className="flex items-center space-x-4 sm:space-x-8 md:space-x-12 flex-grow">
            {showImage && (
              <Link
                href={productUrl}
                dataTestId="wishlist-item-image-link"
                className="flex-none flex flex-col relative"
              >
                <div className="w-32 xs:w-48 sm:w-52 md:w-64">
                  <Img
                    src={product.images[0].href.replace(/1000/g, '600')}
                    alt={product.images[0].alt}
                    width={300}
                  />
                </div>
                <AvailabilityBadge
                  className="hidden 2xs:flex absolute bottom-1 left-1"
                  outOfStock={outOfStock}
                  offline={item.product.offline}
                />
              </Link>
            )}
            <div className="flex-grow pr-4" data-test-id="wishlist-item">
              <div className="flex flex-col w-full">
                <h2 className="font-bold text-puma-black text-sm xs:text-base sm:text-xl md:text-xl leading-tight">
                  {showHeaderSubHeaderProductDetail
                    ? product.header
                    : product.name}
                </h2>
                {showHeaderSubHeaderProductDetail && product.subHeader && (
                  <span className="w-full text-sm xs:text-base sm:text-xl md:text-xl text-puma-gray-60 line-clamp-2">
                    {product.subHeader}
                  </span>
                )}
              </div>
              <p className="text-xs sm:text-sm md:text-lg" data-test-id="color">
                {product.colorName}
              </p>
              <div className="mt-2">
                <>
                  <p className="flex items-center space-x-2">
                    <span className="font-bold text-puma-black text-xs sm:text-sm uppercase">
                      {t('size')}:
                    </span>
                    <span className="text-xs sm:text-sm" data-test-id="size">
                      {product.size.label}
                    </span>
                  </p>
                  <p className="flex items-center space-x-2">
                    <span className="font-bold text-puma-black text-xs sm:text-sm uppercase">
                      {t('price')}:
                    </span>
                    <span
                      className={tw(
                        'text-xs sm:text-sm whitespace-nowrap',
                        isSale ? 'opacity-50 line-through' : ''
                      )}
                      data-test-id="price"
                    >
                      {(!isSale || !isSalePriceElapsed) && (
                        <Price amount={product.price} />
                      )}
                    </span>
                    {isSale && isNumber(product.salePrice) && (
                      <span
                        className="text-xs sm:text-sm whitespace-nowrap font-bold text-puma-red mr-2"
                        data-test-id="sale-price"
                      >
                        <Price amount={product.salePrice} />
                      </span>
                    )}
                  </p>
                  <p className="flex items-center space-x-2">
                    <span className="font-bold text-puma-black text-xs sm:text-sm uppercase">
                      {t('styleNumber')}:
                    </span>
                    <span
                      className={tw('text-xs sm:text-sm whitespace-nowrap')}
                    >
                      {product.styleNumber}
                    </span>
                  </p>
                </>
              </div>

              <div className="flex mt-2 sm:mt-4 space-x-2">
                {update && (
                  <Button
                    className="border-b-0"
                    dataTestId="edit-product-button"
                    disabled={removing || editing}
                    variant="ghost"
                    label={`Edit ${product.name}`}
                    onClick={() => setEditing(true)}
                    icon="pencil"
                    mode="icon"
                  />
                )}
                {remove && (
                  <Button
                    className="border-b-0"
                    dataTestId="remove-product-button"
                    disabled={removing || editing}
                    label={t<'removeProduct'>('removeProduct', {
                      product: product.name,
                    })}
                    variant="ghost"
                    onClick={() => {
                      setRemoving(true);
                    }}
                    icon="trash"
                    mode="icon"
                  />
                )}
              </div>
              {add && (
                <div className="hidden sm:flex">
                  <AddToCartButton
                    className="mt-4 w-full mobile:hidden"
                    disabled={!canAddToCart}
                    isAppExclusive={product.isAppExclusive!}
                    loading={adding}
                    onAddToCart={onAddToCart}
                    outOfStock={outOfStock}
                    presale={isPresale}
                    appOnlyDateTimeFrom={product.appOnlyDateTimeFrom}
                    appOnlyDateTimeTo={product.appOnlyDateTimeTo}
                  />
                </div>
              )}
              {creationDate && (
                <div
                  className="flex justify-end font-bold"
                  data-test-id="wishList-item-date"
                >
                  {t('itemAdded')} {longDateFormat(creationDate, locale)}
                </div>
              )}
            </div>
          </div>
          {add && (
            <div className="sm:hidden">
              <AddToCartButton
                className="mt-4 w-full"
                disabled={!canAddToCart}
                isAppExclusive={product.isAppExclusive!}
                loading={adding}
                onAddToCart={onAddToCart}
                outOfStock={outOfStock}
                presale={isPresale}
                appOnlyDateTimeFrom={product.appOnlyDateTimeFrom}
                appOnlyDateTimeTo={product.appOnlyDateTimeTo}
              />
            </div>
          )}
        </div>
      </div>
      {update && editing && (
        <EditProductModal
          id={item.id}
          price={product.salePrice || product.price}
          masterId={product.masterId}
          key={product.masterId}
          size={product.size.value}
          color={product.colorValue}
          onClose={() => setEditing(false)}
          update={({ size, swatch }) =>
            update({
              input: {
                itemId: id,
                productId: product.masterId,
                quantity,
                swatch,
                size,
              },
            })
          }
        />
      )}
      {removing && (
        <RemoveWishlistItemModal
          onClickOutside={() => setRemoving(false)}
          onConfirm={() => {
            if (remove) {
              remove({
                input: {
                  itemId: id,
                },
              });
              setRemoving(false);
            }
          }}
          product={product}
        />
      )}
    </>
  );
};

export const Wishlist = ({
  t,
  wishList,
  update,
  remove,
}: {
  t: TransType;
  wishList: UseQueryState<{ wishList: WishList }>;
  update: (
    variables: MutationUpdateItemInWishListArgs
  ) => Promise<OperationResult<{ updateItemInWishList: WishListItem }>>;
  remove: (
    variables: MutationRemoveItemFromWishListArgs
  ) => Promise<OperationResult<{ removeItemFromWishList: boolean }>>;
}) => {
  const { addToCart } = useCart({ pause: true });
  const { isMiniCartVisible, closeMiniCart } = useMiniCart();

  useEffect(() => {
    return closeMiniCart;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <section
        className="max-w-screen-lg mx-auto xs:border xs:p-8 xs:rounded-xl w-full"
        aria-label={t('myWishlist')}
      >
        <h1 className="border-b pb-5 flex items-center">
          <span
            className="uppercase text-xl sm:text-2xl"
            data-test-id="wishlist-header"
          >
            {t('myWishlist')}
          </span>
          <span
            className="ml-auto text-sm sm:text-base md:text-lg lg:text-xl"
            data-test-id="wishlist-item-count"
          >
            {wishList.data?.wishList.items.length} {t('items')}
          </span>
        </h1>
        <div id="wishlist-products" className="w-full mx-auto divide-y">
          {wishList.data?.wishList.items.length === 0 ? (
            <div
              id="no-wishlist-items"
              className="flex flex-col w-full items-center justify-center font-display py-32 text-puma-black-500"
            >
              <Icon name="heart" />
              <h1 className="text-puma-black text-center text-3xl font-bold -mt-4 whitespace-pre-wrap">
                {t('yourWishlistIsEmpty')}
              </h1>
            </div>
          ) : (
            <ul>
              {wishList.data?.wishList.items.map(wishlistItem => (
                <li key={wishlistItem.id} className="flex items-start py-5">
                  <WishlistItem
                    key={wishlistItem.id}
                    item={wishlistItem}
                    remove={remove}
                    update={update}
                    add={async (variables: AddToCartVariables) => {
                      const now = new Date();

                      event(AnalyticsEvents.PDP_INTERACTION, {
                        domevent: 'wishlist - transfer',
                        domlabel: `${variables.productId}_${variables.swatch}`,
                        time: `${now.getDate()}-${now.getMonth()}-${now.getFullYear()}`,
                      });

                      const res = await addToCart([
                        {
                          ...variables,
                          size: wishlistItem.product.size.value,
                        },
                      ]);

                      return res;
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
      {isMiniCartVisible && <MiniCart />}
    </>
  );
};

const WishListPage = () => {
  const t = useTranslate();

  const auth = useAuth();
  const [meResult] = useMeQuery({
    pause: auth.guest,
  });

  const [wishList, { update, remove }] = useWishlist();

  return (
    <Page query={[wishList, meResult]}>
      <PageHead
        title={t('pageTitle', {
          title: t('pageTitleFragmentWishlist') ?? t('wishlist'),
        })}
      />
      <Wishlist t={t} wishList={wishList} update={update} remove={remove} />
    </Page>
  );
};

export default withAuth(WishListPage, {
  redirectTo: 'account/login',
  from: 'wishlist',
});
